/**
 * The add-on's own URL parameter (Phase 2 §7.1), reserved in `ParamsObjectSchema` so it never
 * becomes a context value, and the link an order opens with. Shared with the service worker, the
 * entry chunk and the eager graph, so: no imports.
 */
export const OFFLINE_URL_PARAM = 'offline'

/** `?offline=orders`: the device-wide Orders page. */
export const ORDERS_VALUE = 'orders'

/** `?offline=outbox`: a journey link that opens with its offline sheet showing. */
export const OUTBOX_VALUE = 'outbox'

/**
 * The app's own URL parameters: `ParamsObjectSchema`, i.e. commons'
 * `RESERVED_JOURNEY_APP_URL_PARAMS` (a test pins them equal). Copied, not imported: this module
 * imports nothing.
 */
export const APP_URL_PARAMS: readonly string[] = [
  'journeyId',
  'defaultJourneyId',
  'debug',
  'preview',
  'previewId',
  'topBar',
  'mode',
  'isEmbedded',
  'lang',
  'journeyToken',
  'journey_token',
  'nonce',
  'asOrganizationId',
  'embedSource',
  'dataInjectionOptions',
  'revisionId',
  'ep_sso_resume',
  OFFLINE_URL_PARAM
]

/** App parameters an order keeps: its journey, its PQ token (claims are context), the language. */
const KEPT_APP_PARAMS: ReadonlySet<string> = new Set([
  'journeyId',
  'journeyToken',
  'journey_token',
  'lang'
])

/** What the installer-portal frame appends to a launch URL; never part of an order. */
const HOST_PARAMS: ReadonlySet<string> = new Set([
  'parentAuthContext',
  'origin',
  'contact_id',
  'relation_id'
])

const offlineParamOf = (url: string): string | null => {
  try {
    return new URL(url, 'http://offline.local').searchParams.get(
      OFFLINE_URL_PARAM
    )
  } catch {
    return null
  }
}

export const isOrdersUrl = (url: string): boolean =>
  offlineParamOf(url) === ORDERS_VALUE

export const wantsOutbox = (url: string): boolean =>
  offlineParamOf(url) === OUTBOX_VALUE

/**
 * The Orders URL on the page path journey links use (`/` on `journey.*.epilot.io`). Never derived
 * from the build base (`/journey-app/stable/`): the page path lies inside the worker's scope.
 */
export const ordersUrl = ({
  origin,
  pathname
}: {
  origin: string
  pathname: string
}): string => `${origin}${pathname}?${OFFLINE_URL_PARAM}=${ORDERS_VALUE}`

const withOfflineParam = (url: string, value: string | undefined): string => {
  const target = new URL(url)

  if (value === undefined) target.searchParams.delete(OFFLINE_URL_PARAM)
  else target.searchParams.set(OFFLINE_URL_PARAM, value)

  return target.toString()
}

/** An order link that opens its journey with the offline sheet ("not sent" on the Orders page). */
export const outboxUrl = (url: string): string =>
  withOfflineParam(url, OUTBOX_VALUE)

/** The URL without the add-on's parameter, once it was acted on. */
export const withoutOfflineParam = (url: string): string =>
  withOfflineParam(url, undefined)

/** The Orders page opens only URLs of its own origin. */
export const isSameOrigin = (url: string, origin: string): boolean => {
  try {
    return new URL(url).origin === origin
  } catch {
    return false
  }
}

const LANGUAGES = ['de', 'en', 'fr'] as const

/** The Orders page has no journey language: the browser's, among the bundled ones, else German. */
export const ordersLanguage = (
  language: string | undefined
): (typeof LANGUAGES)[number] =>
  LANGUAGES.find((code) => language?.toLowerCase().startsWith(code)) ?? 'de'

/**
 * The link an order opens with (spec §13 A2): the launch URL's page, its journey, its
 * declared context parameters (case-insensitive, as the run identity) and a journey token and
 * language when present. Never the launch URL as is: inside the installer portal it is the iframe
 * URL, whose `parentAuthContext` makes a top-level page wait for a parent that is not there.
 * Without declared keys (a pack from before Plan 2.2): every non-app parameter except the portal
 * frame's. `undefined` when the URL does not parse or names no journey.
 */
export const orderLink = (
  launchUrl: string,
  declaredKeys: readonly string[] | undefined
): string | undefined => {
  let launch: URL

  try {
    launch = new URL(launchUrl)
  } catch {
    return undefined
  }

  const params = launch.searchParams

  if (
    !params.get('journeyId') &&
    !params.get('journeyToken') &&
    !params.get('journey_token')
  ) {
    return undefined
  }

  const declared =
    declaredKeys && new Set(declaredKeys.map((key) => key.toLowerCase()))
  const keeps = (key: string) =>
    KEPT_APP_PARAMS.has(key) ||
    (declared
      ? declared.has(key.toLowerCase())
      : !APP_URL_PARAMS.includes(key) && !HOST_PARAMS.has(key))
  const link = new URLSearchParams()
  const journeyId = params.get('journeyId')

  if (journeyId) link.set('journeyId', journeyId)
  params.forEach((value, key) => {
    if (key !== 'journeyId' && keeps(key)) link.append(key, value)
  })

  return `${launch.origin}${launch.pathname}?${link.toString()}`
}
